import {
  Box,
  Button,
  Dialog,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Icon,
  Skeleton,
  Text,
  toastManager,
} from "@salestrak/ui";
import {
  formatVersionReport,
  useServiceVersions,
} from "#app/lib/use-service-versions";
import { copyToClipboard } from "#app/lib/utils/copy-to-clipboard";

/**
 * "About SalesTrak": the app's build version plus a live probe of every API
 * service's `/version` endpoint. "Copy version report" gives support a
 * paste-ready block for tickets.
 */
export function AboutDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { versions } = useServiceVersions(open);

  const copyReport = async () => {
    try {
      await copyToClipboard(formatVersionReport(versions));
      toastManager.add({ title: "Version report copied", type: "success" });
    } catch {
      toastManager.add({
        title: "Failed to copy version report",
        type: "error",
      });
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogPopup>
        <DialogHeader>
          <Box direction="row" align="center" gap="sm">
            <Icon color="primary" name="info" size="lg" />
            <DialogTitle>About SalesTrak</DialogTitle>
          </Box>
        </DialogHeader>
        <DialogPanel>
          <Box direction="column" gap="2xs">
            {versions.map(({ id, version }, index) => (
              <Box
                key={`${id}-${index}`}
                align="center"
                direction="row"
                gap="md"
                justify="between"
              >
                <Text mono size="sm" variant="muted">
                  {id}
                </Text>
                {version === null ? (
                  <Skeleton className="h-4 w-16" />
                ) : (
                  <Text mono size="sm">
                    {version}
                  </Text>
                )}
              </Box>
            ))}
          </Box>
          <Box direction="row" paddingTop="sm">
            <Button onClick={copyReport} size="sm" variant="outline">
              <Icon name="copy" size="sm" />
              Copy version report
            </Button>
          </Box>
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}
